/*
 * Consent gate for the Google Maps embed in the IT Recycling contact section.
 *
 * The section renders no iframe at all. The embed URL waits in data-src, so the
 * browser reaches Google only after one of two things happens:
 *
 *   1. The visitor grants preference consent. We read that through Shopify's
 *      Customer Privacy API -- preferencesProcessingAllowed() -- and listen for
 *      Shopify's visitorConsentCollected event, which fires when the consent
 *      banner writes a new choice. Consentmo feeds that same API, so the gate
 *      follows the banner without depending on any app-private function.
 *   2. The visitor presses the fallback button, which is an explicit choice to
 *      load the map this once.
 *
 * The Customer Privacy API is injected asynchronously, so a granted consent may
 * not be readable on the first pass. We re-check on a short bounded schedule and
 * stop as soon as the API answers. No polling ever touches a third party.
 */

const CONSENT_POLL_INTERVAL = 200;
const CONSENT_POLL_ATTEMPTS = 25;

class ItrMapConsent extends HTMLElement {
  connectedCallback() {
    this.gate = this.querySelector('[data-map-gate]');
    this.loaded = false;
    this.attempts = 0;

    const button = this.querySelector('[data-map-load]');
    if (button) {
      button.addEventListener('click', () => this.loadMap());
    }

    this.handleConsent = () => {
      if (this.consentGranted()) this.loadMap();
    };
    document.addEventListener('visitorConsentCollected', this.handleConsent);

    this.pollForConsent();
  }

  disconnectedCallback() {
    document.removeEventListener('visitorConsentCollected', this.handleConsent);
    if (this.pollTimer) clearTimeout(this.pollTimer);
  }

  /*
   * Returns null while the Customer Privacy API is still loading, so the caller
   * can tell "not ready yet" apart from a genuine "no".
   */
  consentState() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.preferencesProcessingAllowed !== 'function') return null;

    try {
      return privacy.preferencesProcessingAllowed() === true;
    } catch (error) {
      return null;
    }
  }

  consentGranted() {
    return this.consentState() === true;
  }

  pollForConsent() {
    if (this.loaded) return;

    const state = this.consentState();
    if (state === true) {
      this.loadMap();
      return;
    }

    // A definite "no" is an answer -- wait for visitorConsentCollected instead.
    if (state === false) return;

    this.attempts += 1;
    if (this.attempts >= CONSENT_POLL_ATTEMPTS) return;

    this.pollTimer = setTimeout(() => this.pollForConsent(), CONSENT_POLL_INTERVAL);
  }

  loadMap() {
    if (this.loaded) return;

    const src = this.dataset.src;
    if (!src) return;

    this.loaded = true;
    if (this.pollTimer) clearTimeout(this.pollTimer);

    const frame = document.createElement('iframe');
    frame.className = 'itr-contact__map-embed';
    frame.src = src;
    frame.title = this.dataset.title || '';
    frame.loading = 'lazy';
    frame.referrerPolicy = 'strict-origin-when-cross-origin';

    if (this.gate) this.gate.remove();
    this.appendChild(frame);
  }
}

if (!customElements.get('itr-map-consent')) {
  customElements.define('itr-map-consent', ItrMapConsent);
}
